import type {DiffModes} from 'img'
import {STATUS_TYPES} from '../../render/constants'
import ModeSwitcher from '../../render/mode-switcher'
import Slider from '../../render/slider'
import type Status from '../../render/status'
import {assertHTMLElement, invariant} from '../../render/utils'
import {ImageDiffDimensioner} from './diff-image-dimensioner'
import {ImageLoader} from './image-loader'

type FileState = 'added' | 'deleted'
type TwoUpMetaInfo = {
  addedHeight: HTMLElement
  addedWidth: HTMLElement
  deletedHeight: HTMLElement
  deletedWidth: HTMLElement
}

const MAX_SHELL_WIDTH = 858

class ImageDiff extends ImageLoader {
  private clicked = false
  private loaded: Record<FileState, boolean> = {
    added: false,
    deleted: false,
  }
  private slider: null | Slider = null

  constructor(
    private shell: HTMLElement,
    protected status: Status,
  ) {
    super(status)

    const deleted = this.shell.getAttribute('data-file1') ?? ''
    const added = this.shell.getAttribute('data-file2') ?? ''

    this.fetch(deleted, 'Deleted file', image => this.onImageLoad('deleted', image))
    this.fetch(added, 'Added file', image => this.onImageLoad('added', image))

    new ModeSwitcher<DiffModes>(this.switchMode)
    this.shell.addEventListener(
      'click',
      () => {
        this.clicked = true
      },
      {once: true},
    )
  }

  onImageLoad = (type: FileState, image: HTMLImageElement) => {
    const images = Array.from(document.querySelectorAll(`img.${type}`))

    let loaded = 0
    for (const img of images) {
      const attrs = img.attributes
      for (const attr of attrs) {
        image.setAttribute(attr.name, attr.value)
      }
      const clone = image.cloneNode() as HTMLImageElement
      clone.onload = () => {
        img.parentNode?.replaceChild(clone, img)
        loaded += 1
        this.loaded[type] = loaded === images.length
        this.setReadyIfLoaded()
      }
    }
  }

  setReadyIfLoaded = () => {
    let allLoaded = true
    for (const type in this.loaded) {
      const imgLoaded = this.loaded[type as FileState]
      allLoaded = allLoaded && imgLoaded
    }
    if (allLoaded) {
      this.initViews()
    }
  }

  reInit = (timeout = 100) => {
    if (!this.clicked) {
      this.switchMode('two-up')
      if (timeout < 10000) {
        setTimeout(() => this.reInit(timeout * 2), timeout * 2)
      }
    }
  }

  initViews = () => {
    const added = Array.from(document.querySelectorAll<HTMLImageElement>('img.added'))
    const deleted = Array.from(document.querySelectorAll<HTMLImageElement>('img.deleted'))

    this.initTwoUp(added[0], deleted[0])
    this.initSwipe(added[1], deleted[1])
    this.initOnion(added[2], deleted[2])

    this.status.set(STATUS_TYPES.ready)
    setTimeout(this.reInit, 100)
  }

  initTwoUp = (added: HTMLImageElement, deleted: HTMLImageElement) => {
    const twoUp = ImageDiffDimensioner(added, deleted, imageBox => {
      let twoUpScale = 1
      if (imageBox.width > (MAX_SHELL_WIDTH - 30) / 2) {
        twoUpScale = (MAX_SHELL_WIDTH - 30) / 2 / imageBox.width
      }
      return twoUpScale
    })

    const metaTextEls = ['added', 'deleted'].reduce((memo, type) => {
      for (const attr of ['height', 'width']) {
        const capital = `${attr.slice(0, 1).toUpperCase()}${attr.slice(1)}`
        const element = document.querySelector(`.${type}-meta .${attr}`)
        if (element) {
          memo = {...memo, [`${type}${capital}`]: element}
        }
      }
      return memo
    }, {} as TwoUpMetaInfo)

    if (Object.values(metaTextEls).length !== 4) {
      throw new Error('Invalid DOM structure')
    }

    metaTextEls.addedHeight.textContent = `${twoUp.addedImage.naturalHeight}px`
    metaTextEls.addedWidth.textContent = `${twoUp.addedImage.naturalWidth}px`
    metaTextEls.deletedHeight.textContent = `${twoUp.deletedImage.naturalHeight}px`
    metaTextEls.deletedWidth.textContent = `${twoUp.deletedImage.naturalWidth}px`

    const deletedLabel = document.querySelector<HTMLElement>('.label-deleted')

    if (deletedLabel && twoUp.added.width < 30 && twoUp.deleted.width < 30) {
      deletedLabel.style.right = '25%'
    }
  }

  initSwipe = (added: HTMLImageElement, deleted: HTMLImageElement) => {
    const swipe = ImageDiffDimensioner(added, deleted, imageBox => {
      let swipeScale = 1
      if (imageBox.width > MAX_SHELL_WIDTH - 12) {
        swipeScale = (MAX_SHELL_WIDTH - 12) / imageBox.width
      }
      return swipeScale
    })

    const deletedSwipeFrame = swipe.deletedParent.parentNode
    const addedSwipeFrame = swipe.addedParent.parentNode

    invariant(assertHTMLElement(deletedSwipeFrame))
    invariant(assertHTMLElement(addedSwipeFrame))

    deletedSwipeFrame.setAttribute(
      'style',
      `width: ${swipe.imageBox.width * swipe.scale + 2}px; height: ${swipe.imageBox.height * swipe.scale + 2}px`,
    )

    addedSwipeFrame.setAttribute(
      'style',
      `padding: ${swipe.imageMargins.addedVertMargin * swipe.scale}px 0 0 0;width: ${
        swipe.imageBox.width * swipe.scale + 2
      }px;height: ${swipe.imageBox.height * swipe.scale - swipe.imageMargins.addedVertMargin * swipe.scale + 4}px`,
    )

    const swipeContainerEl = document.querySelector<HTMLElement>('.swipe')

    invariant(assertHTMLElement(swipeContainerEl))
    swipeContainerEl.setAttribute(
      'style',
      `width: ${swipe.imageBox.width * swipe.scale + 2}px;
        height: ${swipe.imageBox.height * swipe.scale + 4}px`,
    )

    const swipeBar = document.querySelector<HTMLElement>('.swipe-bar')
    invariant(assertHTMLElement(swipeBar))

    swipeBar.addEventListener('mousedown', event => {
      event.preventDefault()

      const container = swipeBar.parentNode
      invariant(assertHTMLElement(container))

      const max = container.clientWidth - swipeBar.clientWidth
      const containerEl = container

      function handleMouseUp() {
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
        document.body.style.cursor = 'auto'
      }

      function handleMouseMove(swipeMoveEvent: MouseEvent) {
        swipeMoveEvent.preventDefault()

        let left = swipeMoveEvent.clientX - containerEl.getBoundingClientRect().left
        if (left < 0) {
          left = 0
        }
        if (left > max) {
          left = max
        }

        if (swipeBar) {
          swipeBar.style.left = `${left}px`
        }

        const swipeShell = document.querySelector<HTMLElement>('.swipe-shell')
        const swipeFrame = document.querySelector<HTMLElement>('.swipe-frame')

        if (swipeFrame && swipeShell) {
          swipeShell.style.width = `${swipeFrame.clientWidth - left}px`
        }

        document.addEventListener('mouseup', handleMouseUp)
      }

      document.body.style.cursor = 'pointer'
      document.addEventListener('mousemove', handleMouseMove)
    })
  }

  initOnion = (added: HTMLImageElement, deleted: HTMLImageElement) => {
    const onion = ImageDiffDimensioner(added, deleted, imageBox => {
      let onionScale = 1
      if (imageBox.width > MAX_SHELL_WIDTH - 12) {
        onionScale = (MAX_SHELL_WIDTH - 12) / imageBox.width
      }
      return onionScale
    })

    const onionFrameShell = onion.deletedParent.parentNode
    invariant(assertHTMLElement(onionFrameShell))

    onionFrameShell.setAttribute(
      'style',
      `width: ${onion.imageBox.width * onion.scale + 2}px;
      height: ${onion.imageBox.height * onion.scale + 2}px`,
    )

    const onionSkinEl = document.querySelector<HTMLElement>('.onion-skin')!
    invariant(assertHTMLElement(onionFrameShell))
    onionSkinEl.setAttribute(
      'style',
      `width: ${onion.imageBox.width * onion.scale + 2}px;
        height: ${onion.imageBox.height * onion.scale + 4}px`,
    )

    this.slider = new Slider(
      {
        slide: percentage => {
          if (onion.addedParent) {
            onion.addedParent.style.opacity = `${percentage / 100}`
          }
        },
      },
      {percentage: 100},
    )
  }

  switchMode = (mode: DiffModes) => {
    const viewEls = Array.from(document.querySelectorAll<HTMLElement>('.view'))

    const modeView = viewEls.find(view => view.classList.contains(mode))

    if (!modeView) {
      window.debug('Unknown mode!')
      return
    }

    for (const view of viewEls) {
      view.style.display = 'none'
    }

    const renderBarEl = document.querySelector<HTMLElement>('.render-bar')
    let padding = (renderBarEl?.getBoundingClientRect().height ?? 0) + 40

    if (mode === 'swipe') {
      padding += 14 // Height of drag handle
    } else if (mode === 'onion-skin') {
      padding += 45 // Includes bottom slider
    }

    modeView.style.display = 'block'
    this.status.set(STATUS_TYPES.resize, {height: modeView.getBoundingClientRect().height + padding})
    if (this.slider && mode === 'onion-skin') {
      this.slider.setDragger()
    }
  }
}

export default ImageDiff
